import { exportKsKey } from "@/services/ksKeyService";
import { getErrorMessage } from "@/services/utilities/ServiceError";
import { FC, useState } from "react";
import { toast } from "react-toastify";
import { MANAGE_KEYS } from "../../constants/key-constants";
import { DEF_PG_SIZE_SUB_TABS } from "../../constants/maximum-page-size";
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { MultiSelectOption } from "../../models/MultiSelectOption";
import { DeleteEntityResult } from "../../models/services/DeleteEntityResult";
import { TableColumn } from "../../models/table/TableColumn";
import FullScreenActivityFrame from '../activity-frame/full-screen-activity-spinner';
import VerifyPasswordModal from "../alarm-viewer/VerifyPasswordModal";
import { useDialog } from "../dialog/DialogContext";
import DropdownSelector from "../dropdown-selector/DropdownSelector";
import GenerateImportKeyModal from "../security-policies/keys/generate-key/GenerateImportKeyModal";
import TableTab from "../tabs/table-tab";

interface tabProps {
    dataMethod: any,
    columns: TableColumn[],
    searchDescriptors?: any,
    refreshMethod: any,
    lookup: any,
    title: string,
    deleteMethod?: (id: string, isCustomDeleteKey?: boolean) => Promise<DeleteEntityResult>,
    dataRefresh: (dataMethod?: (query?: any) => Promise<any>) => void
}

export const PolicyKeysTab: FC<tabProps> = ({ dataMethod, columns, searchDescriptors, refreshMethod, lookup, title, deleteMethod, dataRefresh }) => {
    const { translate } = useLocale();
    const [showGenerateKeyWizard, setShowGenerateKeyWizard] = useState(false);
    const manageButtonOptions: MultiSelectOption[] = [
        { value: MANAGE_KEYS.IMPORT_KEY, label: translate.byKey(TranslationKeys.importKey) },
        { value: MANAGE_KEYS.GENERATE_KEY, label: translate.byKey(TranslationKeys.generateKey) },
        { value: MANAGE_KEYS.EXPORT_KEY, label: translate.byKey(TranslationKeys.export) }
    ];
    const [action, setAction] = useState<"IMPORT" | "GENERATE">("GENERATE");
    const [busyExporting, setBusyExporting] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const dialog = useDialog();
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    const onManageOptionClicked = (selectedOption: MultiSelectOption) => {
        // Import a key
        if (selectedOption.value === MANAGE_KEYS.IMPORT_KEY) {
            setAction("IMPORT");
            setShowGenerateKeyWizard(true);
        }
        // Generate a key
        else if (selectedOption.value === MANAGE_KEYS.GENERATE_KEY) {
            setAction("GENERATE");
            setShowGenerateKeyWizard(true);
        }
        // Export key
        else if (selectedOption.value === MANAGE_KEYS.EXPORT_KEY) {
            if (selectedIds.length === 0) {
                dialog.pushError({ title: translate.byKey(TranslationKeys.warning), message: translate.byKey(TranslationKeys.selectAtLeastOneKey) });
                return;
            }

            setIsPasswordModalOpen(true);
        }
    }

    const onVerify = async (password: string) => {
        try {
            let successCount: number = 0;
            setIsPasswordModalOpen(false);
            setBusyExporting(true);
            const keys = await exportKsKey(selectedIds.join(','), password);
            keys.forEach(key => {
                const keyObject = selectedRows.find(row => row.id === key.id);

                if (!keyObject) {
                    toast.error(translate.byKeyFormatted(TranslationKeys.keyExportError, key.id));
                    return;
                }

                const exportedKeyDocument = document.createElement("a");
                exportedKeyDocument.href = `data:application/octet-stream;base64,${key.key}`;
                exportedKeyDocument.download = `${keyObject.name}.key`;
                exportedKeyDocument.click();
                successCount++;
            });

            if (successCount > 0) {
                toast.success(translate.byKey(TranslationKeys.keyExportedSuccessfully));
            }
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            setBusyExporting(false);
        }
    }

    return <>
        {busyExporting ? <FullScreenActivityFrame message={translate.byKey(TranslationKeys.exportingKeysPleaseWait)} /> : null}
        <div className="w-full flex flex-col">
            <div className="mb-3 w-full border-b border-gray-400" />
            <TableContextProvider
                dataMethod={dataMethod}
                deleteMethod={deleteMethod}
                tableDescriptor={columns}
                searchDescriptor={searchDescriptors}
                useLegend
                useTable
                usePagination
                useFilter
                useSearch
                useSort
                manualDataRefresh={refreshMethod}
                maxPageSize={DEF_PG_SIZE_SUB_TABS}
                lookup={lookup}>
                <TableTab
                    title={title}
                    selectRows={true}
                    cyName="keys-table"
                    setSelectedIds={setSelectedIds}
                    selectedIds={selectedIds}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}>
                    <DropdownSelector
                        className="flex px-4 rounded-full shadow text-white bg-sidemenu-focus text-sm items-center"
                        buttonClassName="flex px-8 items-center"
                        onSelected={onManageOptionClicked}
                        options={manageButtonOptions}>
                        {translate.byKey(TranslationKeys.manage)}
                    </DropdownSelector>
                </TableTab>
            </TableContextProvider>
        </div>
        <GenerateImportKeyModal
            key={`${action}-Modal`}
            isOpen={showGenerateKeyWizard}
            onClose={() => setShowGenerateKeyWizard(false)}
            dataRefresh={dataRefresh}
            action={action} />
        <VerifyPasswordModal
            isOpen={isPasswordModalOpen}
            setIsOpen={setIsPasswordModalOpen}
            onVerify={onVerify} />
    </>
};
